import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import * as Sentry from "@sentry/react";

const getErrorMessage = (error: unknown): string => {
	if (!error) return "Unknown error";
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	return JSON.stringify(error);
};

const getStatusText = (error: unknown): string | undefined => {
	if (isRouteErrorResponse(error)) {
		return `${error.status} ${error.statusText}`;
	}
	return undefined;
};

export function RootRouteErrorBoundary() {
	const error = useRouteError();

	useEffect(() => {
		if (error) {
			Sentry.captureException(error);
		}
	}, [error]);

	const statusText = getStatusText(error);
	const message = getErrorMessage(error);

	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
			<div>
				<p className="text-sm font-medium text-muted-foreground">
					{statusText ?? "Something went wrong"}
				</p>
				<h1 className="text-2xl font-semibold">
					We hit a snag loading this page
				</h1>
				<p className="mt-2 text-sm text-muted-foreground">{message}</p>
			</div>
			<div className="flex gap-3">
				<button
					type="button"
					className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
					onClick={() => window.location.reload()}
				>
					Reload
				</button>
				<a
					href="/"
					className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
				>
					Go Home
				</a>
			</div>
		</div>
	);
}
